'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users } from 'lucide-react';
import type { Task, TaskAssigneeUser, TeamMinimal } from '@/types';
import { getTaskAssignees } from '@/lib/task-assignees';

function initials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface AssigneesDisplayProps {
  assignees: TaskAssigneeUser[];
  assignedTeam?: TeamMinimal | null;
  /** Compact: avatar stack + short label; full: slightly larger */
  variant?: 'compact' | 'default';
  /** Board/card: avatars only (no name text). */
  mode?: 'full' | 'avatars-only';
  className?: string;
}

export function AssigneesDisplay({
  assignees,
  assignedTeam,
  variant = 'default',
  mode = 'full',
  className = '',
}: AssigneesDisplayProps) {
  const list = assignees ?? [];
  const avatarClass = variant === 'compact' ? 'h-6 w-6 border-2 border-background text-[9px]' : 'h-7 w-7 border-2 border-background text-[10px]';
  const maxAvatars = 3;

  const hasUsers = list.length > 0;
  const hasTeam = !!assignedTeam;

  // Show both users and team if both are assigned
  if (hasUsers || hasTeam) {
    const userLabel = list.map((a) => a.displayName).join(', ');
    const fullLabel = hasUsers && hasTeam 
      ? `${userLabel}, ${assignedTeam.name}` 
      : hasUsers 
        ? userLabel 
        : assignedTeam?.name ?? '';

    return (
      <div className={`inline-flex items-center gap-2 min-w-0 ${className}`}>
        {/* User avatars */}
        {hasUsers && (
          <div className="flex -space-x-1.5 shrink-0">
            {list.slice(0, maxAvatars).map((a) => (
              <Avatar key={a.id} className={avatarClass} title={a.displayName}>
                <AvatarImage src={a.avatarUrl ?? undefined} alt={a.displayName} />
                <AvatarFallback className="text-[9px]">{initials(a.displayName)}</AvatarFallback>
              </Avatar>
            ))}
            {list.length > maxAvatars && (
              <div
                className={`rounded-full border-2 border-background bg-muted flex items-center justify-center font-medium text-muted-foreground ${variant === 'compact' ? 'h-6 w-6 text-[9px]' : 'h-7 w-7 text-[10px]'}`}
                title={userLabel}
              >
                +{list.length - maxAvatars}
              </div>
            )}
          </div>
        )}
        {/* Team icon */}
        {hasTeam && (
          <div 
            className={`inline-flex items-center gap-1 shrink-0 text-muted-foreground ${variant === 'compact' ? 'text-xs' : 'text-sm'}`}
            title={assignedTeam.name}
          >
            <Users className="h-3.5 w-3.5" />
            {mode === 'full' && !hasUsers && <span className="truncate">{assignedTeam.name}</span>}
          </div>
        )}
        {/* Text label for users (only when no team, or in full mode with users) */}
        {mode === 'full' && hasUsers && (
          <span className={`truncate text-foreground/90 ${variant === 'compact' ? 'text-xs' : 'text-sm'}`} title={fullLabel}>
            {userLabel}
          </span>
        )}
      </div>
    );
  }

  if (mode === 'avatars-only') {
    return null;
  }

  return (
    <span className={`text-muted-foreground ${variant === 'compact' ? 'text-xs' : 'text-sm'} ${className}`}>
      Unassigned
    </span>
  );
}

export function TaskAssigneesCell({
  task,
  variant = 'default',
  mode = 'full',
  className,
}: {
  task: Task;
  variant?: 'compact' | 'default';
  mode?: 'full' | 'avatars-only';
  className?: string;
}) {
  return (
    <AssigneesDisplay
      assignees={getTaskAssignees(task)}
      assignedTeam={task.assignedTeam}
      variant={variant}
      mode={mode}
      className={className}
    />
  );
}
